import { 
  WorkerMessage, 
  WorkerInitMessage, 
  WorkerFrameRateMessage, 
  WorkerRefreshRateMessage, 
  WorkerResolutionMessage, 
  WorkerParameterMessage, 
  WorkerParameterBatchMessage, 
  WorkerStreamMessage, 
  WorkerAudioAnalysisMessage,
  WorkerVideoCanvasSetupMessage,
  WorkerVideoFrameUpdateMessage,
  WorkerVideoConfigUpdateMessage,
  WorkerPerformanceMessage, 
  WorkerSetSceneCodeMessage 
} from './types.js';
import { 
  SliderConfig, 
  ColorConfig, 
  ToggleConfig, 
  SelectConfig, 
  TextConfig, 
  NumberConfig 
} from '../types/index.js';
import { ParameterSystem } from './ParameterSystem.js';
import { InteractionSystem } from './InteractionSystem.js';
import { VideoSystem } from './VideoSystem.js';

// Viji Worker Runtime Implementation
export class VijiWorkerRuntime {
  private canvas: OffscreenCanvas | null = null;
  private ctx: OffscreenCanvasRenderingContext2D | null = null;
  private gl: WebGLRenderingContext | WebGL2RenderingContext | null = null;
  private isRunning = false;
  private frameCount = 0;
  private lastTime = 0;
  private startTime = 0;
  private frameRateMode: 'full' | 'half' = 'full';
  private skipNextFrame = false;
  private screenRefreshRate = 60; // Will be detected
  
  // Debug logging control
  private debugMode = false;

  /**
   * Enable or disable debug logging
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
    // Propagate to child systems
    if (this.videoSystem) this.videoSystem.setDebugMode(enabled);
    if (this.parameterSystem && 'setDebugMode' in this.parameterSystem) {
      (this.parameterSystem as any).setDebugMode(enabled);
    }
    if (this.interactionSystem && 'setDebugMode' in this.interactionSystem) {
      (this.interactionSystem as any).setDebugMode(enabled);
    }
  }

  /**
   * Debug logging helper
   */
  private debugLog(message: string, ...args: any[]): void {
    if (this.debugMode) {
      console.log(message, ...args);
    }
  }
  
  // Effective refresh rate tracking
  private effectiveFrameTimes: number[] = [];
  private lastEffectiveRateReport = 0;
  private effectiveRateReportInterval = 1000; // Report every 1 second

  // Parameter system
  private parameterSystem: ParameterSystem;
  
  // Interaction system (Phase 7)
  private interactionSystem: InteractionSystem;
  
  // Video system (Phase 10) - worker-side video processing
  private videoSystem: VideoSystem;
  
  // Audio state (Phase 5) - receives analysis results from host
  private audioState = {
    isConnected: false,
    volume: { rms: 0, peak: 0 },
    bands: {
      bass: 0, mid: 0, treble: 0, subBass: 0, 
      lowMid: 0, highMid: 0, presence: 0, brilliance: 0
    },
    frequencyData: new Uint8Array(0)
  };

  // Video state is now managed by the worker-side VideoSystem
  
  // Artist API object
  public viji = {
    // Canvas (will be set during init)
    canvas: null as OffscreenCanvas | null,
    ctx: null as OffscreenCanvasRenderingContext2D | null,
    gl: null as WebGLRenderingContext | WebGL2RenderingContext | null,
    width: 0,
    height: 0,
    pixelRatio: 1,
    
    // Timing
    time: 0,
    deltaTime: 0,
    frameCount: 0,
    fps: 60,
    
    // Audio API (Phase 5) - will be set in constructor
    audio: {} as any,
    
    video: {
      isConnected: false,
      currentFrame: null as OffscreenCanvas | null,
      frameWidth: 0,
      frameHeight: 0,
      frameRate: 0,
      getFrameData: () => null,
      faces: [] as any[],
      hands: [] as any[]
    },
    
    // Interaction APIs will be added during construction
    mouse: {} as any,
    keyboard: {} as any,
    touches: {} as any,
    
    // Parameter helper functions (return parameter objects) - delegate to parameter system
    slider: (defaultValue: number, config: SliderConfig) => {
      return this.parameterSystem.createSliderParameter(defaultValue, config);
    },
    
    color: (defaultValue: string, config: ColorConfig) => {
      return this.parameterSystem.createColorParameter(defaultValue, config);
    },
    
    toggle: (defaultValue: boolean, config: ToggleConfig) => {
      return this.parameterSystem.createToggleParameter(defaultValue, config);
    },
    
    select: (defaultValue: string | number, config: SelectConfig) => {
      return this.parameterSystem.createSelectParameter(defaultValue, config);
    },
    
    text: (defaultValue: string, config: TextConfig) => {
      return this.parameterSystem.createTextParameter(defaultValue, config);
    },
    
    number: (defaultValue: number, config: NumberConfig) => {
      return this.parameterSystem.createNumberParameter(defaultValue, config);
    },
    
    // Context selection
    useContext: (type: '2d' | 'webgl'): OffscreenCanvasRenderingContext2D | WebGLRenderingContext | WebGL2RenderingContext | null => {
      if (type === '2d') {
        if (!this.ctx && this.canvas) {
          this.ctx = this.canvas.getContext('2d');
          this.viji.ctx = this.ctx;
        }
        return this.ctx;
      } else if (type === 'webgl') {
        if (!this.gl && this.canvas) {
          this.gl = this.canvas.getContext('webgl2') || this.canvas.getContext('webgl');
          this.viji.gl = this.gl;
          
          // Set initial WebGL viewport
          if (this.gl) {
            this.gl.viewport(0, 0, this.viji.width, this.viji.height);
          }
        }
        return this.gl;
      }
      return null;
    }
  };
  
  constructor() {
    // Initialize parameter system with post message callback
    this.parameterSystem = new ParameterSystem((type: string, data?: any) => {
      this.postMessage(type, data);
    });
    
    // Initialize interaction system
    this.interactionSystem = new InteractionSystem();
    
    // Initialize video system (Phase 10)
    this.videoSystem = new VideoSystem();
    
    // Integrate APIs into viji object
    Object.assign(this.viji, this.interactionSystem.getInteractionAPIs());
    Object.assign(this.viji.video, this.videoSystem.getVideoAPI());
    
    // Initialize audio API with getFrequencyData function
    this.viji.audio = {
      ...this.audioState,
      getFrequencyData: () => this.audioState.frequencyData
    };
    
    this.setupMessageHandling();
  }

  // Reset parameter state (called when loading new scene)
  public resetParameterState(): void {
    this.parameterSystem.resetParameterState();
    this.interactionSystem.resetInteractionState();
    
    // Reset audio state
    this.audioState = {
      isConnected: false,
      volume: { rms: 0, peak: 0 },
      bands: {
        bass: 0, mid: 0, treble: 0, subBass: 0, 
        lowMid: 0, highMid: 0, presence: 0, brilliance: 0
      },
      frequencyData: new Uint8Array(0)
    };
    // Update viji.audio reference
    this.viji.audio = {
      ...this.audioState,
      getFrequencyData: () => this.audioState.frequencyData
    };
    
    // Reset video system
    this.videoSystem.resetVideoState();
    // Update viji.video reference
    Object.assign(this.viji.video, this.videoSystem.getVideoAPI());
  }

  // Send all parameters (from helper functions) to host
  public sendAllParametersToHost(): void {
    this.parameterSystem.sendAllParametersToHost();
  }
  
  private setupMessageHandling(): void {
    self.onmessage = (event: MessageEvent<WorkerMessage>) => {
      const message = event.data;
      
      switch (message.type) {
        case 'init':
          this.handleInit(message);
          break;
        case 'frame-rate-update':
          this.handleFrameRateUpdate(message);
          break;
        case 'refresh-rate-update':
          this.handleRefreshRateUpdate(message);
          break;
        case 'resolution-update':
          this.handleResolutionUpdate(message);
          break;
              case 'set-scene-code':
        this.handleSetSceneCode(message);
        break;
      case 'debug-mode':
        this.setDebugMode(message.data.enabled);
        break;
        case 'parameter-update':
          this.handleParameterUpdate(message);
          break;
        case 'parameter-batch-update':
          this.handleParameterBatchUpdate(message);
          break;
        case 'stream-update':
          this.handleStreamUpdate(message);
          break;
        case 'audio-analysis-update':
          this.handleAudioAnalysisUpdate(message);
          break;
        case 'video-canvas-setup':
          this.handleVideoCanvasSetup(message);
          break;
        case 'video-frame-update':
          this.handleVideoFrameUpdate(message);
          break;
        case 'video-config-update':
          this.handleVideoConfigUpdate(message);
          break;
        case 'mouse-update':
          this.handleMouseUpdate(message);
          break;
        case 'keyboard-update':
          this.handleKeyboardUpdate(message);
          break;
        case 'touch-update':
          this.handleTouchUpdate(message);
          break;
        case 'interaction-enabled':
          this.handleInteractionEnabled(message);
          break;
        case 'performance-update':
          this.handlePerformanceUpdate(message);
          break;
        case 'capture-frame':
          this.handleCaptureFrame(message as any);
          break;
      }
    };
  }
  
  private handleInit(message: WorkerInitMessage): void {
    try {
      this.canvas = message.data.canvas;
      this.viji.canvas = this.canvas;
      
      // Set initial effective resolution (same as canvas size initially)
      this.viji.width = this.canvas.width;
      this.viji.height = this.canvas.height;
      
      // Start render loop
      this.startRenderLoop();
      
      // Send ready response
      this.postMessage('ready', { 
        id: message.id,
        canvasSize: { width: this.canvas.width, height: this.canvas.height }
      });
    } catch (error) {
      this.postMessage('error', {
        id: message.id,
        message: (error as Error).message,
        code: 'INIT_ERROR'
      });
    }
  }
  
  private handleFrameRateUpdate(message: WorkerFrameRateMessage): void {
    if (message.data && message.data.mode) {
      this.frameRateMode = message.data.mode;
      this.debugLog('Frame rate mode updated to:', message.data.mode);
    }
  }
  
  private handleRefreshRateUpdate(message: WorkerRefreshRateMessage): void {
    if (message.data && message.data.screenRefreshRate) {
      this.screenRefreshRate = message.data.screenRefreshRate;
      this.debugLog('Screen refresh rate updated to:', message.data.screenRefreshRate + 'Hz');
    }
  }
  
  private trackEffectiveFrameTime(currentTime: number): void {
    // Keep track of actual render frame times
    this.effectiveFrameTimes.push(currentTime);
    
    // Keep only the last 60 frames for calculation (1 second at 60fps)
    if (this.effectiveFrameTimes.length > 60) {
      this.effectiveFrameTimes.shift();
    }
  }
  
  private reportEffectiveRefreshRate(currentTime: number): void {
    // Report effective refresh rate every second
    if (currentTime - this.lastEffectiveRateReport >= this.effectiveRateReportInterval) {
      if (this.effectiveFrameTimes.length >= 2) {
        // Calculate effective refresh rate from actual frame times
        const totalTime = this.effectiveFrameTimes[this.effectiveFrameTimes.length - 1] - this.effectiveFrameTimes[0];
        const frameCount = this.effectiveFrameTimes.length - 1;
        const effectiveRefreshRate = Math.round((frameCount / totalTime) * 1000);
        
        // Send performance update to core
        this.postMessage('performance-update', {
          effectiveRefreshRate,
          frameRateMode: this.frameRateMode,
          screenRefreshRate: this.screenRefreshRate,
          parameterCount: this.parameterSystem.getParameterCount()
        });
      }
      
      this.lastEffectiveRateReport = currentTime;
    }
  }
  
  private handleResolutionUpdate(message: WorkerResolutionMessage): void {
    if (message.data) {
      // Update canvas resolution directly (effective dimensions * scale)
      if (this.canvas) {
        this.canvas.width = Math.round(message.data.effectiveWidth);
        this.canvas.height = Math.round(message.data.effectiveHeight);
      }
      
      // Update viji dimensions to match actual canvas
      this.viji.width = Math.round(message.data.effectiveWidth);
      this.viji.height = Math.round(message.data.effectiveHeight);
      
      // Update WebGL viewport if context exists
      if (this.gl) {
        this.gl.viewport(0, 0, this.viji.width, this.viji.height);
      }
      
      this.debugLog('Canvas resolution updated to:', this.viji.width + 'x' + this.viji.height);
    }
  }
  
  private handleParameterUpdate(message: WorkerParameterMessage): void {
    if (message.data && message.data.name !== undefined && message.data.value !== undefined) {
      this.parameterSystem.updateParameterValue(message.data.name, message.data.value);
    }
  }

  private handleParameterBatchUpdate(message: WorkerParameterBatchMessage): void {
    if (message.data && message.data.updates) {
      for (const update of message.data.updates) {
        this.parameterSystem.updateParameterValue(update.name, update.value);
      }
      
      // Mark initial values as synced after first batch update
      this.parameterSystem.markInitialValuesSynced();
      this.debugLog('Parameter system initialized successfully');
    }
  }
  
  private handleStreamUpdate(message: WorkerStreamMessage): void {
    // TODO: Handle general stream updates (non-audio)
    this.debugLog('Stream update:', message.data);
  }
  
  private handleAudioAnalysisUpdate(message: WorkerAudioAnalysisMessage): void {
    // Phase 5: Handle audio analysis results from host
    this.audioState = {
      isConnected: message.data.isConnected,
      volume: message.data.volume,
      bands: message.data.bands,
      frequencyData: message.data.frequencyData
    };
    
    // Update viji.audio API
    this.viji.audio = {
      ...this.audioState,
      getFrequencyData: () => this.audioState.frequencyData
    };
  }

  private handleVideoCanvasSetup(message: WorkerVideoCanvasSetupMessage): void {
    // Phase 10: Handle OffscreenCanvas transfer from host to worker-side VideoSystem
    this.videoSystem.handleCanvasSetup({
      offscreenCanvas: message.data.offscreenCanvas,
      width: message.data.width,
      height: message.data.height,
      timestamp: message.data.timestamp
    });
    
    // Update viji.video API with current VideoSystem state
    Object.assign(this.viji.video, this.videoSystem.getVideoAPI());
  }

  private handleVideoFrameUpdate(message: WorkerVideoFrameUpdateMessage): void {
    // Phase 10: Handle ImageBitmap frame from host to worker-side VideoSystem
    this.videoSystem.handleFrameUpdate({
      imageBitmap: message.data.imageBitmap,
      timestamp: message.data.timestamp
    });
    
    // Update viji.video API with current VideoSystem state
    Object.assign(this.viji.video, this.videoSystem.getVideoAPI());
  }

  private handleVideoConfigUpdate(message: WorkerVideoConfigUpdateMessage): void {
    // Phase 10: Handle video configuration updates
    this.videoSystem.handleVideoConfigUpdate({
      ...(message.data.targetFrameRate && { targetFrameRate: message.data.targetFrameRate }),
      ...(message.data.cvConfig && { cvConfig: message.data.cvConfig }),
      ...(message.data.width && { width: message.data.width }),
      ...(message.data.height && { height: message.data.height }),
      ...(message.data.disconnect && { disconnect: message.data.disconnect }),
      timestamp: message.data.timestamp
    });
    
    // ✅ FIX: Update viji.video API with current VideoSystem state after config changes
    // This ensures that disconnection events properly update the scene's video API
    Object.assign(this.viji.video, this.videoSystem.getVideoAPI());
  }
  
  private handlePerformanceUpdate(message: WorkerPerformanceMessage): void {
    // TODO: Handle performance updates in Phase 3
    this.debugLog('Performance update:', message.data);
  }

  /**
   * Handle capture-frame request from host.
   * Produces an ArrayBuffer (image bytes) to send back as transferable.
   */
  private async handleCaptureFrame(message: {
    type: 'capture-frame';
    id: string;
    data: { type?: string; resolution?: number | { width: number; height: number } };
  }): Promise<void> {
    try {
      if (!this.canvas) {
        throw new Error('Canvas not initialized');
      }

      const mimeType = message.data.type || 'image/jpeg';

      // Determine target width/height
      const srcWidth = this.canvas.width;
      const srcHeight = this.canvas.height;
      let targetWidth = srcWidth;
      let targetHeight = srcHeight;

      if (typeof message.data.resolution === 'number') {
        const scale = message.data.resolution > 0 ? message.data.resolution : 1;
        targetWidth = Math.max(1, Math.floor(srcWidth * scale));
        targetHeight = Math.max(1, Math.floor(srcHeight * scale));
      } else if (message.data.resolution && typeof message.data.resolution === 'object') {
        targetWidth = Math.max(1, Math.floor(message.data.resolution.width));
        targetHeight = Math.max(1, Math.floor(message.data.resolution.height));
      }

      // Compute source crop to match aspect ratio if needed
      const srcAspect = srcWidth / srcHeight;
      const dstAspect = targetWidth / targetHeight;
      let sx = 0;
      let sy = 0;
      let sWidth = srcWidth;
      let sHeight = srcHeight;

      if (Math.abs(srcAspect - dstAspect) > 1e-6) {
        if (dstAspect > srcAspect) {
          // Need to crop vertically (top/bottom)
          sHeight = Math.floor(srcWidth / dstAspect);
          sy = Math.floor((srcHeight - sHeight) / 2);
        } else {
          // Need to crop horizontally (left/right)
          sWidth = Math.floor(srcHeight * dstAspect);
          sx = Math.floor((srcWidth - sWidth) / 2);
        }
      }

      // Draw to a temp OffscreenCanvas at target size
      const temp = new OffscreenCanvas(targetWidth, targetHeight);
      const tctx = temp.getContext('2d');
      if (!tctx) throw new Error('Failed to get 2D context');
      // Draw current frame from the main canvas onto temp with crop & scale
      tctx.drawImage(this.canvas as OffscreenCanvas, sx, sy, sWidth, sHeight, 0, 0, targetWidth, targetHeight);

      const blob = await temp.convertToBlob({ type: mimeType });
      const arrayBuffer = await blob.arrayBuffer();

      // Send back as transferable ArrayBuffer to avoid cloning overhead
      (self as any).postMessage({
        type: 'capture-frame-result',
        id: message.id,
        timestamp: Date.now(),
        data: {
          mimeType,
          buffer: arrayBuffer,
          width: targetWidth,
          height: targetHeight
        }
      }, [arrayBuffer]);
    } catch (error) {
      this.postMessage('error', {
        id: message.id,
        message: (error as Error).message,
        code: 'CAPTURE_FRAME_ERROR'
      });
    }
  }
  
  private handleSetSceneCode(message: WorkerSetSceneCodeMessage): void {
    if (message.data && message.data.sceneCode) {
      // Delegate to global scene code function
      (self as any).setSceneCode(message.data.sceneCode);
    }
  }
  
  private startRenderLoop(): void {
    this.isRunning = true;
    this.startTime = performance.now();
    this.lastTime = this.startTime;
    this.renderFrame();
  }
  
  public renderFrame(): void {
    if (!this.isRunning) return;
    
    const currentTime = performance.now();
    
    // Phase 7: Reset frame-based interaction events at start of frame
    this.interactionSystem.frameStart();
    
    // Update frame rate info
    this.viji.fps = this.frameRateMode === 'full' ? this.screenRefreshRate : this.screenRefreshRate / 2;
    
    // Check if we should render this frame
    let shouldRender = true;
    if (this.frameRateMode === 'half') {
      shouldRender = !this.skipNextFrame;
      this.skipNextFrame = !this.skipNextFrame;
    }
    
    if (shouldRender) {
      this.viji.deltaTime = (currentTime - this.lastTime) / 1000;
      this.viji.time = (currentTime - this.startTime) / 1000;
      this.viji.frameCount = ++this.frameCount;
      
      // Track effective frame times for performance calculation
      this.trackEffectiveFrameTime(currentTime);
      
      this.lastTime = currentTime;
      
      try {
        // Call artist render function
        const renderFunction = (self as any).renderFunction;
        if (renderFunction && typeof renderFunction === 'function') {
          renderFunction(this.viji);
        }
      } catch (error) {
        console.error('Render error:', error);
        this.postMessage('error', {
          message: (error as Error).message,
          code: 'RENDER_ERROR',
          stack: (error as Error).stack
        });
      }
    }
    
    // Report effective refresh rate periodically
    this.reportEffectiveRefreshRate(currentTime);
    
    // Schedule next frame check
    requestAnimationFrame(() => this.renderFrame());
  }
  
  private postMessage(type: string, data?: any): void {
    self.postMessage({
      type,
      id: data?.id || `${type}_${Date.now()}`,
      timestamp: Date.now(),
      data
    });
  }

  // Phase 7: Interaction Message Handlers (delegated to InteractionSystem)

  private handleMouseUpdate(message: any): void {
    this.interactionSystem.handleMouseUpdate(message.data);
  }

  private handleKeyboardUpdate(message: any): void {
    this.interactionSystem.handleKeyboardUpdate(message.data);
  }

  private handleTouchUpdate(message: any): void {
    this.interactionSystem.handleTouchUpdate(message.data);
  }

  private handleInteractionEnabled(message: any): void {
    this.interactionSystem.setInteractionEnabled(message.data.enabled);
  }
} 